import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/base/badges/badges";
import { formatTime } from "@/lib/format";

/**
 * Sdílené stavební bloky testovacích nástrojů (Fáze E). Progress kroky, HTTP
 * log, form Field/Row — reuse napříč Test aktivace / OTP-heslo / admin.
 */

/** HTTP log embedovaný v SSE výsledku (shape `TestActivationResult.HttpLogEntry`). */
export interface TestHttpLog {
  method: string;
  endpoint: string;
  statusCode: number | null;
  durationMs: number | null;
  requestHeaders?: Record<string, string> | null;
  requestBody?: unknown;
  responseBody?: unknown;
  timestamp: string;
}

export interface ProgressStep {
  status: string;
  message: string;
  timestamp: string;
  step?: number | null;
  totalSteps?: number | null;
}

export function progressColor(status: string): "success" | "error" | "blue" | "gray" {
  if (status === "COMPLETED") return "success";
  if (status === "FAILED") return "error";
  if (status === "STARTED") return "gray";
  return "blue";
}

export function ProgressSteps({ steps }: { steps: ProgressStep[] }) {
  const { t } = useTranslation();
  if (steps.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-primary">{t("testing.progress")}</h2>
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-3 text-sm">
          <Badge type="pill-color" size="sm" color={progressColor(s.status)}>
            {s.status}
          </Badge>
          <span className="text-tertiary tabular-nums">{formatTime(s.timestamp)}</span>
          {s.step != null && s.totalSteps != null && (
            <span className="text-tertiary tabular-nums">
              [{s.step}/{s.totalSteps}]
            </span>
          )}
          <span className="text-secondary">{s.message}</span>
        </div>
      ))}
    </div>
  );
}

export function TestHttpLogs({ logs }: { logs: TestHttpLog[] }) {
  const { t } = useTranslation();
  if (!logs || logs.length === 0) return null;
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-primary">{t("testing.httpLog")}</h2>
      {logs.map((log, i) => (
        <details key={i} className="rounded-lg bg-secondary p-3 ring-1 ring-secondary">
          <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm">
            <span className="font-mono font-semibold text-secondary">{log.method}</span>
            <span className="min-w-0 flex-1 truncate text-secondary">{log.endpoint}</span>
            {log.statusCode != null && (
              <Badge type="pill-color" size="sm" color={log.statusCode < 400 ? "success" : "error"}>
                {log.statusCode}
              </Badge>
            )}
            {log.durationMs != null && <span className="text-tertiary tabular-nums">{log.durationMs} ms</span>}
          </summary>
          <div className="mt-2 grid gap-3 lg:grid-cols-2">
            <LogBlock title="Request" data={log.requestBody} />
            <LogBlock title="Response" data={log.responseBody} />
          </div>
        </details>
      ))}
    </div>
  );
}

function LogBlock({ title, data }: { title: string; data: unknown }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold text-tertiary">{title}</h4>
      <pre className="max-h-64 overflow-auto rounded-md bg-primary p-2 font-mono text-xs text-secondary ring-1 ring-secondary">
        {data == null ? "—" : typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

const inputBase =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";
export const inputCls = inputBase;

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{label}</span>
      {children}
      {hint && <span className="text-xs text-tertiary">{hint}</span>}
    </label>
  );
}

export function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="text-tertiary">{label}:</dt>
      <dd className={`text-secondary ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}
